import { BaseViewModel } from '../../../../models/base/base-view-model';
import { Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, exhaustMap, filter, map, startWith } from 'rxjs/operators';
import { CompanyDomainModel } from '../../../../domainModels/company-domain-model';
import { UserDomainModel } from '../../../../domainModels/user-domain-model';
import { AppOutOfDateService } from '../../../../services/app-out-of-date.service';
import { RestrictedLocationMismatchService } from '../../../../services/restricted-location-mismatch.service';
import { NavigationEnd, Router } from '@angular/router';
import { exists } from '../../../../functions/exists';

@Injectable()
export class DashboardViewModel extends BaseViewModel {
  constructor(
    private router: Router,
    private appOutOfDateService: AppOutOfDateService,
    private restrictedLocationMismatchService: RestrictedLocationMismatchService,
    private userDomainModel: UserDomainModel,
    private companyDomainModel: CompanyDomainModel
  ) {
    super();
    // Listen to employees data
    const eSub = combineLatest([this.userDomainModel.user$, this.companyDomainModel.employees$])
      .pipe(
        map(([user, employees]) => {
          const validUser = user?.accountConfirmed === true;
          const validSession = user?.session?.validSession() === true;
          // The roster comes from an admin-only endpoint, so for anyone below admin the request can only fail.
          const hasAdminAccess = user?.hasAdminAccess() === true;
          return !employees && validUser && validSession && hasAdminAccess;
        }),
        distinctUntilChanged(),
        debounceTime(100),
        filter(needsEmployees => needsEmployees),
        // exhaustMap, not switchMap: user$ republishes mid-flight, and switchMap cancelled the request
        // before its tap could seed the employees, so the guard never closed and the fetch re-fired.
        exhaustMap(() => this.companyDomainModel.adminGetEmployees$().pipe(catchError(() => of(null))))
      )
      .subscribe();
    this.pushSub(eSub);
  }

  private readonly _sideNavCollapsed = new BehaviorSubject(false);
  public readonly sideNavCollapsed$ = this._sideNavCollapsed as Observable<boolean>;
  connectToSideNavCollapsed = (collapsed: boolean) => this._sideNavCollapsed.next(collapsed);

  public readonly toastMargin$ = this.sideNavCollapsed$.pipe(
    map(sideNavCollapsed => {
      return sideNavCollapsed ? '1.5rem 6rem 1.5rem 6.75rem' : '1.5rem 6rem 1.5rem 15.5rem';
    })
  );

  public readonly appIsOutOfDate$ = this.appOutOfDateService.appIsOutOfDate$;

  public readonly restrictedLocationMismatch$ = this.restrictedLocationMismatchService.restrictedLocationMismatch$;

  public readonly blurTheScreen$ = combineLatest([this.appIsOutOfDate$, this.restrictedLocationMismatch$]).pipe(
    map(([appIsOutOfDate, restrictedLocationMismatch]) => {
      return appIsOutOfDate || restrictedLocationMismatch;
    }),
    distinctUntilChanged()
  );

  public readonly isHighGuide$ = this.router.events.pipe(
    filter(event => event instanceof NavigationEnd),
    map((navigationEndEvent: NavigationEnd) => navigationEndEvent?.url),
    startWith(this.router.url),
    distinctUntilChanged(),
    map(url => url?.startsWith('/high-guide')),
    distinctUntilChanged()
  );

  public readonly sidebarVisible$ = this.userDomainModel.user$.pipe(
    map(user => {
      return exists(user) ? user?.accountConfirmed || user?.session?.validSession() : false;
    })
  );
}
